
import Vue, { PropType } from "vue";
import ToolbarGroup from "./components/ToolbarGroup.vue";
import {
  addDynamicMenuItem,
  dynamicMenu,
  initDynamicMenu,
  replaceDynamicMenuItem,
  updateDynamicMenu,
} from "./store/store";
import { ConfigToolbar, ConfigToolbarRoute, DynamicItem, MainMenuItem } from "./interfaces/Toolbar";

export default Vue.extend({
  components: { ToolbarGroup },
  props: {
    menu: {
      type: Array as PropType<MainMenuItem[]>,
    },
    config: {
      type: Object as PropType<ConfigToolbar>,
    },
  },
  data() {
    return {
      hasFinishMounted: false,
      hasToResetLimit: false,
      isMounted: false,
      mainMenuSize: 0,
      menuId: this.config.id,
    };
  },
  async mounted() {
    if (this.isDynamicMenuShown) {
      this.isMounted = true;
      this.mainMenuSize = ((this.$refs.menu as Vue).$el as HTMLElement).getBoundingClientRect().right;

      if (dynamicMenu.isFirstLoad) {
        initDynamicMenu(this.menuId);
        dynamicMenu.isFirstLoad = false;
        this.$nextTick(() => {
          this.setTabsSize();
        });
      }

      if (this.matchRoute) {
        await this.createOrReplaceTab();
      }
      this.hasFinishMounted = true;
    }
  },
  computed: {
    availableSize(): number {
      // 88 === scrollbar size (+-20) + triggerBtn size (32) && position right (36)
      return this.$vuetify.breakpoint.width - this.mainMenuSize - 88;
    },
    dynamicItemsLimit(): number | undefined {
      // when undefined => limit is default to 52 === max items displayed on 4k screen in FSEND (site-transfer IDs 1->52)
      return !this.isMounted || !this.tabsSize.length || this.hasToResetLimit ? undefined : dynamicMenu.itemsLimit;
    },
    dynamicMenu(): DynamicItem[] {
      return dynamicMenu.items;
    },
    isDynamicMenuShown() {
      return "isDynamicMenuShown" in this.config ? this.config.isDynamicMenuShown : true;
    },
    matchRoute(): string | null {
      const match = this.$route.matched.find(({ path }) => this.routes.includes(path));
      return match ? match.path : null;
    },
    routes(): string[] {
      const routes: string[] = [];
      for (const { paths } of this.config.routes) {
        routes.push(...paths);
      }
      return routes;
    },
    tabsSize(): number[] {
      return dynamicMenu.tabsSize;
    },
  },
  methods: {
    async assignPopup(dynamicItem: DynamicItem, route: ConfigToolbarRoute): Promise<void> {
      const popup = await this.getToolbarPopup(route);
      if (popup) {
        Object.assign(dynamicItem, {
          popup: popup,
        });
      }
    },
    async createOrReplaceTab(): Promise<void> {
      const route = this.getMatchingRoute();

      if (route) {
        const itemToCheck = route.getItem();
        const itemInMenu = this.dynamicMenu.find(item => item.id === itemToCheck.id);

        if (!itemInMenu) {
          const dynamicItem: DynamicItem = Object.assign({}, itemToCheck, {
            label: await this.getToolbarLabel(route),
            position: this.generatePosition(),
          });
          await this.assignPopup(dynamicItem, route);
          addDynamicMenuItem(this.menuId, dynamicItem);
          this.hasToResetLimit = true;
          this.$nextTick(() => {
            this.setTabsSize();
          });
        } else if (itemInMenu.path !== itemToCheck.path) {
          const dynamicItem: DynamicItem = Object.assign({}, itemInMenu, {
            path: itemToCheck.path,
          });
          await this.assignPopup(dynamicItem, route);
          replaceDynamicMenuItem(this.menuId, itemInMenu, dynamicItem);
        }
      }
    },
    generatePosition(): number {
      if (this.dynamicMenu.length > 0) {
        const positions = this.dynamicMenu.map((item: DynamicItem) => item.position);
        return Math.max(...positions) + 1;
      }
      return 1;
    },
    getMatchingRoute(): ConfigToolbarRoute | undefined {
      return this.config.routes.find(({ paths }) => this.matchRoute && paths.includes(this.matchRoute));
    },
    async getToolbarLabel(route: ConfigToolbarRoute): Promise<string> {
      return await route.getLabel();
    },
    async getToolbarPopup(route: ConfigToolbarRoute): Promise<string | null> {
      return route.getPopup ? await route.getPopup() : null;
    },
    handleDelete(itemToDelete: DynamicItem): void {
      updateDynamicMenu(
        this.menuId,
        this.dynamicMenu.filter(item => item.id != itemToDelete.id)
      );
      this.hasToResetLimit = true;
      this.$nextTick(() => {
        this.setTabsSize();
      });

      if (this.$route.fullPath == itemToDelete.path) {
        const itemToRedirect =
          this.dynamicMenu.find((item: DynamicItem) => item.position > itemToDelete.position) ??
          this.dynamicMenu[this.dynamicMenu.length - 1];
        this.$router.push(itemToRedirect ? { path: itemToRedirect.path } : { name: this.config.pushToPage });
      }
    },
    setDynamicItemsLimit(): void {
      let sizeCount = 0;
      let limit = 0;
      for (const [i, size] of this.tabsSize.entries()) {
        if (limit === i && sizeCount + size < this.availableSize) {
          sizeCount += size;
          limit = i + 1;
        }
      }
      dynamicMenu.itemsLimit = limit;
    },
    setTabsSize(): void {
      if (this.dynamicMenu.length) {
        this.$nextTick(() => {
          const tabsSize = [] as number[];
          const links = ((this.$refs.dynamicMenu as Vue).$el as HTMLElement).getElementsByTagName("a");
          for (const link of links) {
            tabsSize.push(link.getBoundingClientRect().width);
          }
          dynamicMenu.tabsSize = tabsSize;
          this.setDynamicItemsLimit();
        });
      }
      this.hasToResetLimit = false;
    },
  },
  watch: {
    "$route.fullPath": {
      async handler() {
        if (this.hasFinishMounted && this.matchRoute) {
          this.createOrReplaceTab();
        }
      },
      immediate: true,
    },
    "$vuetify.breakpoint.width": {
      handler() {
        this.hasToResetLimit = true;
        this.$nextTick(() => {
          this.setTabsSize();
        });
      },
    },
  },
});
